import { useState, useCallback, useEffect, useRef } from 'react';
import { api } from '../api/client';
import { buildFolderTree, normalizeFolderPath } from '../utils/docFolders';

// useDocumentFolders — the live folder structure for a deal / deal detail.
//
// There is no folder taxonomy in the app. A deal's structure is generated by
// Claude from the documents it holds, and the API serves that plan merged with
// the folders the deal really has (created in Harvi, present in SharePoint, or
// already in use by its documents). This hook is the single client-side entry
// point to that list plus the create / rename / delete operations that keep
// Harvi and SharePoint in step.
//
// It degrades rather than breaks: if the endpoint is unreachable the folder list
// is simply empty and `error` is set, so a picker says so instead of silently
// offering folders that may not exist.
//
//   { folders, tree, loading, error, sharePointAvailable, hasPlan,
//     reload, refresh, createFolder, renameFolder, deleteFolder }
//
// `refresh()` re-reads past the server's short SharePoint discovery cache — use
// it after something outside Harvi may have changed the tree.
export function useDocumentFolders({ dealId, dealDetailId, detailType, enabled = true } = {}) {
  const [folders, setFolders] = useState([]);
  const [tree, setTree]       = useState([]);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError]     = useState(null);
  const [spAvailable, setSpAvailable] = useState(false);
  const [hasPlan, setHasPlan] = useState(false);
  const reqRef = useRef(0);

  const load = useCallback(async ({ refresh = false, keepOnError = false } = {}) => {
    if (!enabled || (!dealId && !dealDetailId)) {
      setLoading(false);
      setFolders([]); setTree([]);
      return;
    }
    // Guard against an out-of-order response overwriting a newer one (the panel
    // re-requests whenever the user switches deal detail).
    const seq = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (dealId)       qs.set('dealId', dealId);
      if (dealDetailId) qs.set('dealDetailId', dealDetailId);
      if (detailType)   qs.set('detailType', detailType);
      if (refresh)      qs.set('refresh', '1');
      const res = await api.get(`document-folders?${qs.toString()}`);
      if (seq !== reqRef.current) return;
      const list = res?.folders || [];
      setFolders(list);
      setTree(res?.tree?.length ? res.tree : buildFolderTree(list));
      setSpAvailable(!!res?.sharePointAvailable);
      setHasPlan(!!res?.hasPlan);
    } catch (e) {
      if (seq !== reqRef.current) return;
      setError(e.message);
      // After a create the list on screen is known-good (and holds the folder
      // just made) — a failed re-read must not blank it.
      if (keepOnError) return;
      setFolders([]); setTree([]);
      setSpAvailable(false); setHasPlan(false);
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [enabled, dealId, dealDetailId, detailType]);

  useEffect(() => { load(); }, [load]);

  const scopeBody = useCallback(
    (extra) => ({ ...(dealId ? { dealId } : {}), ...(dealDetailId ? { dealDetailId } : {}), ...extra }),
    [dealId, dealDetailId]
  );

  // Creates the folder in SharePoint too — that's the point. The server records
  // it and adds it to its cached SharePoint listing, so a plain reload shows it;
  // forcing refresh here re-walked the deal's whole SharePoint tree after every
  // create, which on a large deal was most of the wait. The new folder is also
  // merged in locally, so it appears even if that reload fails.
  const createFolder = useCallback(async (path, label) => {
    const res = await api.post('document-folders', scopeBody({ path: normalizeFolderPath(path), label }),
      { timeout: 60000 });
    const created = res?.folder;
    if (created?.path) {
      setFolders(prev => {
        if (prev.some(f => f.path.toLowerCase() === created.path.toLowerCase())) return prev;
        const next = [...prev, { ...created, sources: ['custom'], docCount: 0 }];
        setTree(buildFolderTree(next));
        return next;
      });
    }
    await load({ keepOnError: true });
    return created;
  }, [scopeBody, load]);

  const renameFolder = useCallback(async (path, newPath) => {
    const res = await api.put('document-folders',
      scopeBody({ path: normalizeFolderPath(path), newPath: normalizeFolderPath(newPath) }));
    await load({ refresh: true });
    return res;
  }, [scopeBody, load]);

  const deleteFolder = useCallback(async (path) => {
    const qs = new URLSearchParams();
    if (dealId)       qs.set('dealId', dealId);
    if (dealDetailId) qs.set('dealDetailId', dealDetailId);
    qs.set('path', normalizeFolderPath(path));
    const res = await api.del(`document-folders?${qs.toString()}`);
    await load({ refresh: true });
    return res;
  }, [dealId, dealDetailId, load]);

  return {
    folders, tree, loading, error,
    sharePointAvailable: spAvailable,
    hasPlan,
    reload:  () => load(),
    refresh: () => load({ refresh: true }),
    createFolder, renameFolder, deleteFolder,
  };
}
